import { create } from 'zustand';

interface PlatformUser {
  id: string;
  email: string;
  username: string;
  displayName: string;
  role: string;
  emailVerified?: boolean;
}

export interface ActionResult {
  ok: boolean;
  error?: string;
}

interface AuthState {
  token: string | null;
  user: PlatformUser | null;
  isLoading: boolean;
  error: string | null;
  notice: string | null;

  login: (email: string, password: string) => Promise<void>;
  signup: (
    email: string,
    username: string,
    password: string,
    acceptTerms: boolean,
    confirmAge: boolean,
    inviteCode: string
  ) => Promise<void>;
  logout: () => void;
  fetchMe: () => Promise<void>;
  clearError: () => void;
  clearNotice: () => void;

  verifyEmail: (token: string) => Promise<ActionResult>;
  resendVerification: () => Promise<ActionResult>;
  forgotPassword: (email: string) => Promise<ActionResult>;
  resetPassword: (token: string, password: string) => Promise<ActionResult>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<ActionResult>;
  changeEmail: (password: string, newEmail: string) => Promise<ActionResult>;
  confirmEmailChange: (token: string) => Promise<ActionResult>;
  downloadMyData: () => Promise<ActionResult>;
  deleteAccount: (password: string) => Promise<ActionResult & { deleteAfter?: string }>;
}

const API = '/api/platform/auth';

function storeSession(data: { token: string; refreshToken?: string }) {
  localStorage.setItem('platform_token', data.token);
  if (data.refreshToken) localStorage.setItem('platform_refresh', data.refreshToken);
}

export const useAuthStore = create<AuthState>((set, get) => {
  async function post(
    path: string,
    body: unknown,
    auth = false
  ): Promise<{ ok: boolean; data: any }> {
    try {
      const token = get().token;
      const res = await fetch(`${API}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(auth && token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      return { ok: res.ok, data };
    } catch {
      return { ok: false, data: { error: 'Could not reach the server' } };
    }
  }

  const result = (r: { ok: boolean; data: any }, fallback: string): ActionResult =>
    r.ok ? { ok: true } : { ok: false, error: r.data?.error || fallback };

  return {
    token: localStorage.getItem('platform_token'),
    user: null,
    isLoading: false,
    error: null,
    notice: null,

    login: async (email, password) => {
      set({ isLoading: true, error: null, notice: null });
      try {
        const res = await fetch(`${API}/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Login failed');

        storeSession(data);
        set({
          token: data.token,
          user: data.user,
          isLoading: false,
          notice: data.deletionCancelled
            ? 'Welcome back. Your account deletion has been cancelled.'
            : null,
        });
      } catch (err) {
        set({ isLoading: false, error: err instanceof Error ? err.message : 'Login failed' });
      }
    },

    signup: async (email, username, password, acceptTerms, confirmAge, inviteCode) => {
      set({ isLoading: true, error: null });
      try {
        const res = await fetch(`${API}/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, username, password, acceptTerms, confirmAge, inviteCode }),
        });
        const data = await res.json();
        if (data.code === 'invite_required' || data.code === 'invite_invalid') {
          throw new Error(
            'This invite code is not valid. Check the email it came in, or ask for a new one.'
          );
        }
        if (!res.ok) throw new Error(data.error || 'Signup failed');

        storeSession(data);
        set({ token: data.token, user: data.user, isLoading: false });
      } catch (err) {
        set({ isLoading: false, error: err instanceof Error ? err.message : 'Signup failed' });
      }
    },

    logout: () => {
      localStorage.removeItem('platform_token');
      localStorage.removeItem('platform_refresh');
      set({ token: null, user: null });
    },

    fetchMe: async () => {
      const token = get().token;
      if (!token) return;

      try {
        const res = await fetch(`${API}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (res.status === 401) get().logout();
          return;
        }
        const user = await res.json();
        set({ user });
      } catch {
        // Network error - don't log out, just ignore
      }
    },

    clearError: () => set({ error: null }),
    clearNotice: () => set({ notice: null }),

    verifyEmail: async (token) => {
      const r = await post('/verify-email', { token });
      if (r.ok) {
        const user = get().user;
        if (user) set({ user: { ...user, emailVerified: true } });
      }
      return result(r, 'Could not verify your email');
    },

    resendVerification: async () =>
      result(await post('/resend-verification', {}, true), 'Could not send the email'),

    forgotPassword: async (email) =>
      result(await post('/forgot-password', { email }), 'Could not send the email'),

    resetPassword: async (token, password) =>
      result(await post('/reset-password', { token, password }), 'Could not reset the password'),

    changePassword: async (currentPassword, newPassword) => {
      const r = await post('/change-password', { currentPassword, newPassword }, true);
      if (r.ok) {
        storeSession(r.data);
        set({ token: r.data.token });
      }
      return result(r, 'Could not change the password');
    },

    changeEmail: async (password, newEmail) =>
      result(await post('/change-email', { password, newEmail }, true), 'Could not change email'),

    confirmEmailChange: async (token) => {
      const r = await post('/confirm-email-change', { token });
      if (r.ok) {
        const user = get().user;
        if (user) set({ user: { ...user, email: r.data.email, emailVerified: true } });
      }
      return result(r, 'Could not confirm the new email');
    },

    downloadMyData: async () => {
      try {
        const res = await fetch(`${API}/export`, {
          headers: { Authorization: `Bearer ${get().token}` },
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          return { ok: false, error: data.error || 'Could not download your data' };
        }
        const blob = await res.blob();
        const name =
          res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ??
          'lune-account.zip';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
        URL.revokeObjectURL(url);
        return { ok: true };
      } catch {
        return { ok: false, error: 'Could not reach the server' };
      }
    },

    deleteAccount: async (password) => {
      const r = await post('/delete-account', { password }, true);
      if (!r.ok) return result(r, 'Could not delete the account');
      get().logout();
      return { ok: true, deleteAfter: r.data.deleteAfter };
    },
  };
});
